import type { CustomerRole } from './account'

/**
 * Customer RBAC capability model (security controls C23/C24/C25/C26, ADR-0013).
 *
 * Authorization is **capability-based**, not role-name based: a privileged action
 * declares the {@link Capability} it requires, and the {@link ROLE_CAPABILITIES}
 * map below is the single source of truth resolved by the one decision function
 * {@link can} (C26 — centralize the authz decision). This keeps least-privilege
 * auditable in one place instead of scattering role checks across routes.
 *
 * These are all **customer-world** capabilities. Staff capabilities are a SEPARATE
 * authorization world (C24) and are intentionally absent here — staff RBAC is
 * deferred to the staff/admin realm (S0.14/E11). Because no staff capability exists
 * in this vocabulary, a customer session can never be granted one.
 */
export const CAPABILITIES = [
  // Baseline — granted to every authenticated customer role.
  'catalog:read',
  'account:read',
  // Buyer transactional baseline.
  'cart:manage',
  'quote:request',
  // Account-wide read of the tenant's orders (list + detail), UID-63/ADR-0036.
  // Baseline — reading your account's order history is not a transactional privilege.
  'order:read',
  // Differentiated, least-privilege actions (C25).
  'order:submit', // Purchaser-only in production; also granted to engineer as a DEMO-ONLY override (see ROLE_CAPABILITIES below).
  'bom:upload', // Engineer-only — upload a BOM.
  'rfq:submit', // Engineer-only — submit an RFQ.
  'part:write', // Engineer-only — mutate a CONFIRMED library part (config edit / authored revision). Distinct from read (ADR-0059 §2).
  'user:manage', // account_admin-only — manage the account's users.
  'workspace:manage', // account_admin-only — create/manage workspaces + membership (UID-48a).
] as const

export type Capability = (typeof CAPABILITIES)[number]

export function isCapability(value: unknown): value is Capability {
  return typeof value === 'string' && (CAPABILITIES as readonly string[]).includes(value)
}

/**
 * The buyer baseline every customer role shares. Roles compose this explicitly
 * (below) rather than inheriting via a rank — `roles.ts` `ROLE_RANK` is explicitly
 * NOT a capability hierarchy, so permission sets are declared per role.
 */
const BUYER_BASELINE = [
  'catalog:read',
  'account:read',
  'cart:manage',
  'quote:request',
  'order:read',
] as const

/**
 * role → allowed capabilities. **Deny-by-default**: any capability absent from a
 * role's set is denied (C25).
 *
 * `account_admin` is intentionally STRICT (ADR-0013): it adds `user:manage` and
 * `workspace:manage` to the baseline but NOT the transactional capabilities
 * (`order:submit`, `bom:upload`, `rfq:submit`) — separation of duties. An admin
 * who must also transact holds a second role (membership is a join, ADR-0002).
 * This posture is revisitable.
 *
 * `workspace:manage` (UID-48a, human-locked) is account_admin-only. It is the
 * ACCOUNT-capability axis; it COMPOSES with the orthogonal per-workspace
 * `workspace_role` (owner/member, ADR-0068) checked at the app layer —
 * e.g. member add/remove requires BOTH `workspace:manage` AND workspace ownership.
 * The two vocabularies never merge (no `workspace:bom:*` strings).
 */
export const ROLE_CAPABILITIES: Record<CustomerRole, readonly Capability[]> = {
  buyer: [...BUYER_BASELINE],
  // DEMO-ONLY OVERRIDE (not a production RBAC decision): `order:submit` is added
  // here so the demo account `engineer@demo.misumi.test` can complete the SAME
  // checkout/order-placement flows as `purchaser@demo.misumi.test`, for demo
  // walkthroughs that need one login to reach the whole cart→checkout→order
  // path without switching accounts. This REMOVES the engineer/purchaser
  // separation-of-duties boundary the rest of this file documents (see the
  // `account_admin` comment below for the general pattern this deviates from)
  // — it is a deliberate, scoped relaxation for demo/eval purposes, not a
  // reinstated product decision. Revert before this posture is relied on for
  // anything beyond a demo (i.e. drop `'order:submit'` from this array to
  // restore the least-privilege split).
  //
  // DEMO-ONLY OVERRIDE #2 (UID-587, same posture as the `order:submit` one
  // above and UID-436): `user:manage` + `workspace:manage` are added so the
  // demo account `engineer@demo.misumi.test` can also reach the account-admin
  // surfaces (user management, workspace management) without switching to
  // `account_admin@demo.misumi.test` mid-walkthrough. This collapses the
  // ADR-0013 separation-of-duties boundary that keeps admin management OUT of
  // a transacting role — again a deliberate, scoped demo/eval relaxation, not a
  // production RBAC decision. `workspace:manage` still COMPOSES with the
  // per-workspace `workspace_role` (owner/member, ADR-0068) at the app layer,
  // so some member-management actions additionally need workspace ownership.
  // Revert before relied on beyond a demo (drop `'user:manage'` +
  // `'workspace:manage'` from this array to restore least privilege).
  engineer: [
    ...BUYER_BASELINE,
    'bom:upload',
    'rfq:submit',
    'part:write',
    'order:submit',
    'user:manage',
    'workspace:manage',
  ],
  purchaser: [...BUYER_BASELINE, 'order:submit'],
  account_admin: [...BUYER_BASELINE, 'user:manage', 'workspace:manage'],
}

/**
 * The single customer-authorization decision point (C26). Deny-by-default:
 * returns true only when `capability` is explicitly granted to `role`.
 *
 * Fails closed even for a `role` outside {@link CustomerRole} — TypeScript blocks
 * that at compile time, but `can()` is also exported for frontend UI gating where
 * a role may arrive from the wire/store; an unknown role denies rather than throws.
 */
export function can(role: CustomerRole, capability: Capability): boolean {
  return (ROLE_CAPABILITIES[role] ?? []).includes(capability)
}
